<script setup lang="ts">
import MiiaFwConfig from '@/configs/config'
import { computed } from 'vue'
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { useDisasterStore } from '@/stores/disasterStore'
import { storeToRefs } from 'pinia'
import { blackSloganWithRed } from '@/assets/img/imageBase64'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { signout } from '@/features/miiaFw/utils/miiaFwCognito'
import { useRouter } from 'vue-router'
import { useStoreUtils } from '@/composables/common/store/useStoreUtils'
import { ROUTER_PATHS } from '@/constants/router/paths'
import { useAerialImageAnnotationModal } from '@/composables/annotation/useAerialImageAnnotationModal'
import { useGeoserverStatusStore } from '@/stores/geoserverStatusStore'
import { useMapPointCloudStore } from '@/stores/mapPointCloudStore'
import { useRefreshStore } from '@/stores/refreshStore'
import { useAerialAreasStore } from '@/stores/aerialAreasStore'
import { useLoadingView } from '@/features/miiaFw/composables/useLoadingView'
import { GEO_SERVER_STATUS_LABEL } from '@/constants/api/geoserver/geoserverConstants'
import { useUserAttributes } from '@/features/miiaFw/composables/useUserAttributes'
import { useEmergencyResultCsvStore } from '@/stores/emergencyResultCsvStore'
import { ExportProcessState } from '@/types/api/emergencyResultCsv/exportStatusType'

const { setLoadingView } = useLoadingView()
const router = useRouter()
const miiaFwStore = useMiiaFwStore()
const disasterStore = useDisasterStore()
const commonModalStore = useCommonModalStore()
const emergencyResultCsvStore = useEmergencyResultCsvStore()
const geoserverStatusStore = useGeoserverStatusStore()
const { attributes, isAuthorized } = storeToRefs(miiaFwStore)
const { govCode, disasterId, disasterName } = storeToRefs(disasterStore)
const { exportProcessStatus } = storeToRefs(emergencyResultCsvStore)

const userName = computed(() =>
  isAuthorized.value && attributes.value ? attributes.value.user_name : ''
)
const authorizedDisasterName = computed(() => (isAuthorized.value ? disasterName.value : ''))

/**
 * @callback openSignout
 * @description サインアウト用のモーダル画面を開く
 * @param { } - 引数なし
 * @return { } 戻り値なし
 */
const openSignoutConfirm = async (): Promise<void> => {
  const storeUtils = useStoreUtils()
  await commonModalStore
    .openConfirmModal({ body: 'サインアウトします。\nよろしいですか？' })
    // OK押下時
    .then(async () => {
      await signout()
      if (router.currentRoute.value.path !== ROUTER_PATHS.login)
        router.push({ path: ROUTER_PATHS.login }) // 同じページに遷移させるとNavigation Errorになる。
      storeUtils.allReset()
    })
    // キャンセル押下時は何もしない
    .catch(() => {})
}

/**
 * アノテーション選択モーダルを開く
 * @param なし
 * @returns なし
 */
const openAnnotationModal = async (): Promise<void> => {
  const aerialImageAnnotationModal = useAerialImageAnnotationModal()
  await aerialImageAnnotationModal.open(true)
}

/** 応急危険度判定結果一括ダウンロードボタンの非活性化判定をする */
const disabledDownloadEmergencyResultCsv = computed(() => {
  return (
    !disasterId.value ||
    exportProcessStatus.value === ExportProcessState.WAITING ||
    exportProcessStatus.value === ExportProcessState.PROCESSING
  )
})

/** 応急危険度判定結果一括ダウンロードを実行する */
const downloadEmergencyResultCsv = async (): Promise<void> => {
  const { getUserAttributesFromStore } = useUserAttributes()
  const attribute = await getUserAttributesFromStore()
  const govCode = attribute.attribute['disaster-commonattr'].govCode
  if (!disasterId.value) {
    return
  }
  // CSV出力を開始
  await emergencyResultCsvStore.exportCsv(govCode, disasterId.value)
}

/**
 * 最新のデータに更新する
 */
const refreshLatestData = async (): Promise<void> => {
  if (!govCode.value || !disasterId.value) return

  const mapPointCloudStore = useMapPointCloudStore()
  const aerialAreasStore = useAerialAreasStore()
  const refreshStore = useRefreshStore()

  try {
    // 円グラフデータ取得APIを実行し、Storeに保存
    await mapPointCloudStore.fetchData(govCode.value, disasterId.value)
    // 空撮範囲
    await aerialAreasStore.fetch({ govCode: govCode.value, disasterId: disasterId.value })
    // 更新通知
    refreshStore.triggerRefresh()
  } finally {
    setLoadingView(false)
  }
}
</script>

<template>
  <nav class="navbar navbar-expand-lg">
    <div class="header-margin">
      <div class="header-logo">
        <img
          :src="`data:image/jpg;base64,${blackSloganWithRed}`"
          width="152"
          height="42"
          alt="FUJIFILM Value from Innovation"
        />
      </div>
      <span class="sys-name">{{ MiiaFwConfig.config.sysName }}</span>
      <div v-if="isAuthorized" class="header-btn-group">
        <button
          @click="refreshLatestData"
          class="btn btn-primary ff-btn-primary"
          :disabled="!govCode || !disasterId"
        >
          最新のデータに更新する
        </button>
        <button @click="openAnnotationModal" class="btn btn-primary ff-btn-primary">
          AI倒壊判定の再判定
        </button>
        <button
          class="btn btn-primary ff-btn-primary"
          @click="downloadEmergencyResultCsv"
          :disabled="disabledDownloadEmergencyResultCsv"
        >
          応急危険度判定結果一括ダウンロード
          <CommonSpinner v-if="disabledDownloadEmergencyResultCsv" small />
        </button>
      </div>
    </div>
    <div class="header-right" v-if="isAuthorized">
      <span class="geoserver-status"
        >【ジオサーバー状態：{{
          geoserverStatusStore.geoserverStatus
            ? GEO_SERVER_STATUS_LABEL[geoserverStatusStore.geoserverStatus].label
            : ''
        }}】</span
      >
      <span class="disaster-name">{{ authorizedDisasterName }}</span>
      <span class="user-name">{{ userName }}</span>
      <a class="btn my-2 my-sm-0 mr-sm-2 btn-info signoutButton" @click="openSignoutConfirm"
        ><font-awesome-icon icon="sign-out-alt" />&nbsp;{{ $t('Auth.signOut') }}</a
      >
    </div>
  </nav>
</template>

<style lang="scss" scoped>
nav {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  box-shadow: 0 4px 12px 0 rgba(19, 37, 85, 0.16);
  min-height: 60px;
  padding: 0 !important;
}

.header-margin {
  display: flex;
  align-items: center;
  flex-shrink: 1;
  flex-basis: 820px;
  min-width: 220px;
  flex-wrap: wrap;
}

.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 180px;
  flex-wrap: wrap;
}

:deep(.header-margin) .commonDropdown {
  width: 100%;
}

.header-logo {
  margin: 12.5px;
  margin-bottom: 9.92px;
}

.sys-name {
  margin-right: 1%;
  font-size: 1.6rem;
  white-space: nowrap;
}

.disaster-name,
.user-name {
  margin-right: 8px;
  font-size: 1.6rem;
  white-space: nowrap;
}

.geoserver-status {
  margin-right: 8px;
  font-size: 1.6rem;
  white-space: nowrap;
}

.circle-info {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3rem;
  height: 3rem;
  border: 1px solid;
  font-size: 2rem;
  border-radius: 50%;
  margin: 10px;
}

.header-btn-group button {
  margin-right: 10px;
}

@media (max-width: 1300px) {
  nav {
    flex-wrap: wrap;
    height: auto;
    padding: 0.5rem 1rem;
  }

  .header-margin,
  .header-right {
    width: 100% !important;
    flex-basis: 100% !important;
    justify-content: flex-start !important;
    margin-top: 8px;
  }

  nav > *:first-child {
    margin-top: 0; /* 最初の要素だけ上マージン除去 */
  }

  .header-btn-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  .header-btn-group button {
    min-width: 120px;
    margin-right: 0 !important;
  }

  .geoserver-status,
  .disaster-name,
  .user-name,
  .sys-name {
    white-space: normal;
  }
}
</style>
